/**
 * FindPanel — the World Atlas's advanced search for structures (2026-09-24).
 *
 * "Add an advanced search for specific structures on the world map." The Find tab of the map's side panel: words
 * (every word prefix-matches the name, the kind, the settlement, the function or the model — "clinic", "armory aur",
 * "cantina aethon", a player's name for their houses), category chips with live counts, the civilisation, a scope
 * (anywhere, within a distance of you, what is in view) and an order (nearest, name, largest). Results carry their
 * bearing from you; a row flies the map to the thing at building zoom and flashes it; ◆ sets a waypoint on it that
 * the in-world indicator then follows. "Show on map" paints every match as a dot, at any zoom.
 *
 * A structure clicked on the map is PINNED at the top of the tab with the same two actions. The DOM is built once and
 * kept (the map's side panel re-renders its other tabs every 2.5 s; this one only re-queries when its inputs, the
 * index or your position move).
 */
import { CATEGORY_INFO, CATEGORY_ORDER, searchMap, distanceText, compassOf, type MapCategory, type MapEntry, type MapQuery } from '../../lib/map_structures.js';

export interface FindHost {
  entries(): readonly MapEntry[];
  /** '' when the index is complete, else a note ("loading the map…"). */
  status(): string;
  me(): { x: number; z: number };
  viewBounds(): { x0: number; z0: number; x1: number; z1: number };
  fly(e: MapEntry): void;
  waypoint(e: MapEntry): void;
  showMatches(m: MapEntry[] | null): void;
}

type Scope = 'any' | '1000' | '5000' | '25000' | '100000' | 'view';
const PAGE = 100;
const MAP_MATCHES_MAX = 5000;

const esc = (v: string): string => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const CSS = `
#wm-find{display:flex;flex-direction:column;gap:7px;font:12px/1.4 var(--ax-font);color:var(--ax-text)}
#wm-find input[type=search],#wm-find select{background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);font:12px var(--ax-font);padding:4px 7px;min-width:0}
#wm-find input[type=search]{width:100%;box-sizing:border-box}
#wm-find .wf-row{display:flex;gap:6px;align-items:center;flex-wrap:wrap}
#wm-find .wf-chips{display:flex;flex-wrap:wrap;gap:4px}
#wm-find .wf-chip{display:inline-flex;align-items:center;gap:5px;padding:2px 7px;border-radius:999px;border:1px solid var(--ax-line);background:var(--ax-bg2);color:var(--ax-soft);cursor:pointer;font:600 10.5px var(--ax-font)}
#wm-find .wf-chip[aria-pressed=true]{border-color:var(--ax-accent);background:color-mix(in srgb, var(--ax-accent) 22%, var(--ax-bg2));color:var(--ax-text)}
#wm-find .wf-chip[disabled]{opacity:.35;cursor:default}
#wm-find .wf-dot{width:8px;height:8px;border-radius:50%;flex:0 0 auto}
#wm-find .wf-n{color:var(--ax-muted);font:10px var(--ax-mono)}
#wm-find .wf-head{display:flex;justify-content:space-between;color:var(--ax-muted);font-size:11px;border-bottom:1px solid var(--ax-line);padding-bottom:3px}
#wm-find .wf-list{display:flex;flex-direction:column}
#wm-find .wf-item{display:grid;grid-template-columns:10px 1fr auto auto auto;gap:6px;align-items:center;padding:4px 2px;border-bottom:1px solid var(--ax-line);cursor:pointer}
#wm-find .wf-item:hover,#wm-find .wf-item:focus-visible{background:var(--ax-sel);outline:none}
#wm-find .wf-name{font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
#wm-find .wf-sub{display:block;font-weight:400;color:var(--ax-muted);font-size:10.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
#wm-find .wf-dist{color:var(--ax-soft);font:10.5px var(--ax-mono);white-space:nowrap}
#wm-find .wf-btn{background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);cursor:pointer;font:600 11px var(--ax-font);padding:2px 6px}
#wm-find .wf-btn:hover{border-color:var(--ax-accent)}
#wm-find .wf-pin{border:1px solid var(--ax-accent);border-radius:var(--ax-radius);padding:7px 9px;background:color-mix(in srgb, var(--ax-accent) 10%, var(--ax-bg2))}
#wm-find .wf-pin b{font-size:13px}
#wm-find .wf-empty{color:var(--ax-muted);padding:10px 2px}
#wm-find .wf-more{align-self:center;margin-top:6px}
`;

export class FindPanel {
  readonly root: HTMLDivElement;
  private readonly input: HTMLInputElement;
  private readonly chipsEl: HTMLDivElement;
  private readonly civSel: HTMLSelectElement;
  private readonly scopeSel: HTMLSelectElement;
  private readonly sortSel: HTMLSelectElement;
  private readonly onMap: HTMLInputElement;
  private readonly head: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly more: HTMLButtonElement;
  private readonly pinEl: HTMLDivElement;
  private readonly chips = new Map<MapCategory, HTMLButtonElement>();
  private readonly cats = new Set<MapCategory>();
  private limit = PAGE;
  private hits: MapEntry[] = [];
  private pinned: MapEntry | null = null;
  private timer: number | null = null;
  /** What the last query ran against: re-query only when one of these moves. */
  private lastKey = '';
  /** The last query's numbers (the probe reads them). */
  readonly stats = { total: 0, shown: 0, queries: 0, ms: 0 };

  constructor(private readonly host: FindHost) {
    if (!document.getElementById('wm-find-css')) {
      const st = document.createElement('style');
      st.id = 'wm-find-css';
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    const root = document.createElement('div');
    root.id = 'wm-find';
    root.innerHTML =
      `<div class="wf-pin" hidden></div>` +
      `<input type="search" placeholder="find a structure — clinic, cantina, armory, starport, a town…" aria-label="Search structures">` +
      `<div class="wf-chips" role="group" aria-label="Kinds of structure"></div>` +
      `<div class="wf-row">` +
      `<select data-f="civ" aria-label="Civilization"><option value="">either civ</option><option value="a">Aurelian</option><option value="b">Borean</option></select>` +
      `<select data-f="scope" aria-label="Where"><option value="any">anywhere</option><option value="1000">within 1 km</option><option value="5000">within 5 km</option><option value="25000">within 25 km</option><option value="100000">within 100 km</option><option value="view">in view</option></select>` +
      `<select data-f="sort" aria-label="Order"><option value="distance">nearest</option><option value="name">by name</option><option value="size">largest</option></select>` +
      `<label style="display:inline-flex;gap:4px;align-items:center;color:var(--ax-soft);font-size:11px"><input type="checkbox" data-f="map" checked>show on map</label>` +
      `</div>` +
      `<div class="wf-head"><span data-f="count"></span><span data-f="note"></span></div>` +
      `<div class="wf-list" role="list"></div>` +
      `<button class="wf-btn wf-more" hidden>show more</button>`;
    this.root = root;
    this.pinEl = root.querySelector('.wf-pin') as HTMLDivElement;
    this.input = root.querySelector('input[type=search]') as HTMLInputElement;
    this.chipsEl = root.querySelector('.wf-chips') as HTMLDivElement;
    this.civSel = root.querySelector('select[data-f=civ]') as HTMLSelectElement;
    this.scopeSel = root.querySelector('select[data-f=scope]') as HTMLSelectElement;
    this.sortSel = root.querySelector('select[data-f=sort]') as HTMLSelectElement;
    this.onMap = root.querySelector('input[data-f=map]') as HTMLInputElement;
    this.head = root.querySelector('.wf-head') as HTMLDivElement;
    this.list = root.querySelector('.wf-list') as HTMLDivElement;
    this.more = root.querySelector('.wf-more') as HTMLButtonElement;

    for (const c of CATEGORY_ORDER) {
      const b = document.createElement('button');
      b.className = 'wf-chip';
      b.dataset.cat = c;
      b.setAttribute('aria-pressed', 'false');
      b.innerHTML = `<span class="wf-dot" style="background:${CATEGORY_INFO[c].colour}"></span>${esc(CATEGORY_INFO[c].label)} <span class="wf-n"></span>`;
      b.onclick = () => {
        if (this.cats.has(c)) this.cats.delete(c); else this.cats.add(c);
        b.setAttribute('aria-pressed', String(this.cats.has(c)));
        this.limit = PAGE;
        this.run(true);
      };
      this.chips.set(c, b);
      this.chipsEl.appendChild(b);
    }
    this.input.addEventListener('input', () => { this.limit = PAGE; this.later(); });
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // the game's keys stay out of the search box
      if (e.key === 'Enter') { this.run(true); if (this.hits[0]) this.host.fly(this.hits[0]); }
      if (e.key === 'Escape') { this.input.value = ''; this.run(true); }
    });
    for (const s of [this.civSel, this.scopeSel, this.sortSel]) s.addEventListener('change', () => { this.limit = PAGE; this.run(true); });
    this.onMap.addEventListener('change', () => this.run(true));
    this.more.onclick = () => { this.limit += PAGE; this.run(true); };
    this.list.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const item = t.closest('[data-i]') as HTMLElement | null;
      if (!item) return;
      const hit = this.hits[Number(item.dataset.i)];
      if (!hit) return;
      if (t.closest('[data-act=wp]')) this.host.waypoint(hit);
      else this.host.fly(hit);
    });
    this.list.addEventListener('keydown', (e) => {
      const item = (e.target as HTMLElement).closest('[data-i]') as HTMLElement | null;
      if (!item || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      const hit = this.hits[Number(item.dataset.i)];
      if (hit) this.host.fly(hit);
    });
    this.pinEl.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (!this.pinned) return;
      if (t.closest('[data-act=fly]')) this.host.fly(this.pinned);
      else if (t.closest('[data-act=wp]')) this.host.waypoint(this.pinned);
      else if (t.closest('[data-act=unpin]')) this.pin(null);
    });
  }

  /** Focus the words box (the toolbar's Find button). */
  focus(text?: string): void {
    if (text !== undefined && text !== this.input.value) { this.input.value = text; this.limit = PAGE; this.run(true); }
    this.input.focus();
  }

  /** Set the pressed category chips exactly (the lever; a click toggles one). */
  setCategories(list: readonly MapCategory[]): void {
    this.cats.clear();
    for (const c of list) this.cats.add(c);
    for (const [c, b] of this.chips) b.setAttribute('aria-pressed', String(this.cats.has(c)));
    this.limit = PAGE;
    this.run(true);
  }

  /** Pin a structure clicked on the map (null clears). */
  pin(e: MapEntry | null): void {
    this.pinned = e;
    this.renderPin();
  }

  private renderPin(): void {
    const e = this.pinned;
    if (!e) { this.pinEl.hidden = true; this.pinEl.innerHTML = ''; return; }
    const me = this.host.me();
    const info = CATEGORY_INFO[e.category];
    const size = e.r !== undefined ? `${Math.round(e.r)} m border` : e.w !== undefined && e.d !== undefined ? `${Math.round(e.w)} × ${Math.round(e.d)} m${e.h ? `, ${Math.round(e.h)} m tall` : ''}` : '';
    this.pinEl.hidden = false;
    this.pinEl.innerHTML =
      `<div style="display:flex;justify-content:space-between;gap:8px;align-items:start"><div><b>${esc(e.name)}</b><br>` +
      `<span style="display:inline-flex;gap:5px;align-items:center;color:var(--ax-soft)"><span class="wf-dot" style="background:${info.colour}"></span>${esc(info.label)}${e.place && e.place !== e.name ? ` · ${esc(e.place)}` : ''}</span>` +
      (size ? `<br><span style="color:var(--ax-muted)">${size}</span>` : '') +
      `<br><span style="color:var(--ax-hi)">${this.bearing(e, me)}</span></div>` +
      `<button class="wf-btn" data-act="unpin" title="Close">✕</button></div>` +
      `<div class="wf-row" style="margin-top:6px"><button class="wf-btn" data-act="fly">⌖ show</button><button class="wf-btn" data-act="wp">◆ waypoint &amp; follow</button></div>`;
  }

  /** Where the rows' distances were last written (a walk re-labels them without re-running the search). */
  private labelledAt: { x: number; z: number } | null = null;
  private relabel(me: { x: number; z: number }): void {
    if (this.hits.length === 0 || (this.labelledAt && Math.hypot(me.x - this.labelledAt.x, me.z - this.labelledAt.z) < 25)) return;
    this.labelledAt = { x: me.x, z: me.z };
    const spans = this.list.querySelectorAll<HTMLElement>('[data-i] .wf-dist');
    spans.forEach((el, i) => { const e = this.hits[i]; if (e) el.textContent = this.bearing(e, me); });
  }

  private bearing(e: MapEntry, me: { x: number; z: number }): string {
    const d = Math.hypot(e.x - me.x, e.z - me.z);
    return d < 5 ? 'you are here' : `${distanceText(d)} ${compassOf(e.x - me.x, e.z - me.z)}`;
  }

  private later(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => { this.timer = null; this.run(true); }, 140);
  }

  /** The query the controls describe (exported for the probe through `__wxMap.find`). */
  query(): MapQuery {
    const me = this.host.me();
    const scope = this.scopeSel.value as Scope;
    return {
      text: this.input.value,
      categories: this.cats,
      civ: this.civSel.value as '' | 'a' | 'b',
      withinM: scope === 'any' || scope === 'view' ? 0 : Number(scope),
      fromX: me.x, fromZ: me.z,
      sort: this.sortSel.value as MapQuery['sort'],
      limit: this.limit,
    };
  }

  private active(): boolean { return this.input.value.trim() !== '' || this.cats.size > 0; }

  /** Re-run the query when its inputs, the index, the view (scope "in view") or your position moved. */
  refresh(force = false): void { this.run(force); }

  private run(force: boolean): void {
    const all = this.host.entries();
    const me = this.host.me();
    const scope = this.scopeSel.value as Scope;
    const vb = scope === 'view' ? this.host.viewBounds() : null;
    // Your position is part of the query only when it orders or bounds it (nearest, within N km); otherwise a walk
    // re-labels the rows' distances in place — the tab re-ran its whole search every 2.5 s while you moved.
    const posMatters = this.sortSel.value === 'distance' || (scope !== 'any' && scope !== 'view');
    const key = [all.length, this.input.value, [...this.cats].join(','), this.civSel.value, scope, this.sortSel.value, this.limit, this.onMap.checked,
      posMatters ? Math.round(me.x / 50) : '', posMatters ? Math.round(me.z / 50) : '', vb ? [vb.x0, vb.z0, vb.x1, vb.z1].map((v) => Math.round(v / 20)).join(',') : ''].join('|');
    if (!force && key === this.lastKey) { this.relabel(me); this.renderPin(); return; }
    this.lastKey = key;
    const t0 = performance.now();
    const q = this.query();
    const note = this.host.status();
    const inView = vb ? (e: MapEntry): boolean => e.x >= vb.x0 && e.x <= vb.x1 && e.z >= vb.z0 && e.z <= vb.z1 : null;
    // ONE pass: the chips' counts (each chip says how many it would add under the other filters — a chip with none is
    // disabled), the page and the map's dots (lib/map_structures searchMap).
    const res = searchMap(all, q, this.onMap.checked ? MAP_MATCHES_MAX : 0, inView);
    const counts = res.counts;
    for (const [c, b] of this.chips) {
      const n = counts.get(c) ?? 0;
      (b.querySelector('.wf-n') as HTMLElement).textContent = n > 0 ? n.toLocaleString() : '';
      b.disabled = n === 0 && !this.cats.has(c);
    }
    let total = 0;
    if (this.active()) {
      total = res.total;
      this.hits = res.hits;
      this.host.showMatches(this.onMap.checked ? res.mapHits : null);
    } else {
      this.hits = [];
      this.host.showMatches(null);
    }
    this.stats.total = total; this.stats.shown = this.hits.length; this.stats.queries++; this.stats.ms = performance.now() - t0;
    (this.head.querySelector('[data-f=count]') as HTMLElement).textContent = this.active()
      ? `${total.toLocaleString()} match${total === 1 ? '' : 'es'}${total > this.hits.length ? ` · showing ${this.hits.length}` : ''}`
      : `${all.length.toLocaleString()} places on the map`;
    (this.head.querySelector('[data-f=note]') as HTMLElement).textContent = note;
    this.more.hidden = !(this.active() && total > this.hits.length);
    if (!this.active()) {
      this.list.innerHTML = `<div class="wf-empty">Type what you are looking for, or pick a kind above. Every word must start a word of the place: <i>clinic</i>, <i>armory aurelian</i>, <i>cantina</i> + a town's name, a player's name for their buildings.</div>`;
    } else if (this.hits.length === 0) {
      this.list.innerHTML = `<div class="wf-empty">Nothing matches${note ? ` yet (${esc(note)})` : ''}. Fewer words, another kind, or a wider area.</div>`;
    } else {
      this.labelledAt = { x: me.x, z: me.z };
      this.list.innerHTML = this.hits.map((e, i) => {
        const info = CATEGORY_INFO[e.category];
        const sub = [e.place !== e.name ? e.place : '', info.label].filter((s) => s !== '').join(' · ');
        return `<div class="wf-item" role="listitem" tabindex="0" data-i="${i}" title="Show it on the map">` +
          `<span class="wf-dot" style="background:${info.colour}"></span>` +
          `<span style="min-width:0"><span class="wf-name">${esc(e.name)}</span><span class="wf-sub">${esc(sub)}</span></span>` +
          `<span class="wf-dist">${this.bearing(e, me)}</span>` +
          `<button class="wf-btn" data-act="fly" title="Show it on the map" aria-label="Show ${esc(e.name)} on the map">⌖</button>` +
          `<button class="wf-btn" data-act="wp" title="Set a waypoint here and follow it" aria-label="Set a waypoint at ${esc(e.name)}">◆</button></div>`;
      }).join('');
    }
    this.renderPin();
  }
}
